import {StorageService, StorageType} from '@core/services/storage.service';
import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class SessionService {

    private readonly _apiTokenKey = 'api-token';
    private readonly _refreshTokenKey = 'refresh-token';
    private readonly _userKey = 'user';

    private _user = new BehaviorSubject<Api.User>(null);

    constructor(private _storage: StorageService) {
        const user = this._storage.get<Api.User>(this._userKey, StorageType.Cookie);
        if (user) {
            this._user.next(user);
        }
    }

    get isAdmin() {
        return this.tokenPayload && this.tokenPayload['role'] === 'admin';
    }

    get isAgent() {
        return this.tokenPayload && this.tokenPayload['role'] === 'agent';
    }

    get isPlatformAdmin() {
        return this.tokenPayload && this.tokenPayload['role'] === 'platform_admin';
    }

    get isUser() {
        return this.tokenPayload && this.tokenPayload['role'] === 'user';
    }

    get tokenPayload() {
        return this.decode(this.apiToken);
    }

    get apiToken() {
        return this._storage.get(this._apiTokenKey, StorageType.Cookie);
    }

    get refreshToken() {
        return this._storage.get(this._refreshTokenKey, StorageType.Cookie);
    }

    get user() {
        return this._user.asObservable();
    }

    setTokens(token: string, refreshToken: string) {
        // Set the token if the token exists.
        if (token) {
            this._storage.set<string>(this._apiTokenKey, token, StorageType.Cookie);
        }

        // Set the refresh token if the refresh token exists.
        if (refreshToken) {
            this._storage.set<string>(this._refreshTokenKey, refreshToken, StorageType.Cookie);
        }
    }

    setUser(user: Api.User) {
        // Push the next user.
        this._user.next(user);

        // Save the user to the storage.
        this._storage.set<Api.User>(this._userKey, user, StorageType.Cookie);
    }

    deleteTokensAndUser() {
        this._storage.remove(this._apiTokenKey, StorageType.Cookie);
        this._storage.remove(this._refreshTokenKey, StorageType.Cookie);
        this._storage.remove(this._userKey, StorageType.Cookie);

        this._user.next(null);
    }

    private decode(token: string | null) {
        try {
            return JSON.parse(
                this.urlDecode(token.split('.')[1])
            );
        } catch (error) {
            return null;
        }
    }

    private urlDecode(token: string | null) {
        const value = token || '';
        let output = value.replace(/-/g, '+').replace(/_/g, '/');

        switch (output.length % 4) {
            case 0:
                break;
            case 2:
                output += '==';
                break;
            case 3:
                output += '=';
                break;
            default:
                throw 'Illegal base64url string!';
        }

        try {
            return this.baseDecodeUnicode(output);
        } catch (error) {
            return atob(output);
        }
    }

    private baseDecodeUnicode = (value: string) => {
        return decodeURIComponent(atob(value).replace(/(.)/g, (m, p) => {
            let code = p.charCodeAt(0).toString(16).toUpperCase();
            if (code.length < 2) {
                code = '0' + code;
            }
            return '%' + code;
        }));
    };

}
